import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch } from 'react-icons/fi';
import Layout from '../components/Layout';
import CategoryTable from '../components/Tables/CategoryTable';
import { renderIcon } from '../utils/iconRenderer';
import { Category } from '../types/models';

const CategoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([
    {
      id: '1',
      name: 'T-Shirts',
      description: 'Casual and formal t-shirts collection',
      parentCategory: undefined,
      isTopCollection: true,
      isTopServices: false,
      collections: ['Summer Collection', 'Winter Collection'],
      tags: ['casual', 'formal', 'cotton'],
      images: ['https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=500&fit=crop'],
      videos: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: '2',
      name: 'Hoodies',
      description: 'Comfortable hoodies for all seasons',
      parentCategory: undefined,
      isTopCollection: true,
      isTopServices: true,
      collections: ['Winter Wear'],
      tags: ['warm', 'comfortable', 'polyester'],
      images: ['https://images.unsplash.com/photo-1556821552-5f63b1c2c723?w=500&h=500&fit=crop'],
      videos: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: '3',
      name: 'Caps',
      description: 'Stylish caps and hats',
      parentCategory: undefined,
      isTopCollection: false,
      isTopServices: false,
      collections: [],
      tags: ['style', 'fashion'],
      images: [],
      videos: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCategories = categories.filter(
    category =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (category: Category) => {
    navigate(`/category/edit/${category.id}`, { state: { category } });
  };

  const handleDelete = (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      setCategories(prev => prev.filter(c => c.id !== categoryId));
    }
  };

  const handleView = (category: Category) => {
    console.log('View category:', category);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
            <p className="text-gray-600 mt-1">Manage product categories and collections</p>
          </div>
          <button
            onClick={() => navigate('/category/add')}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            {renderIcon(FiPlus, { size: 20 })}
            <span>Add Category</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
          <div className="relative">
            <span className="absolute left-3 top-3 text-gray-400">{renderIcon(FiSearch, { size: 20 })}</span>
            <input
              type="text"
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
            <p className="text-gray-600 text-sm font-medium">Total Categories</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{categories.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
            <p className="text-gray-600 text-sm font-medium">Top Collections</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">
              {categories.filter(c => c.isTopCollection).length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
            <p className="text-gray-600 text-sm font-medium">Top Services</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {categories.filter(c => c.isTopServices).length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
            <p className="text-gray-600 text-sm font-medium">With Images</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">
              {categories.filter(c => c.images.length > 0).length}
            </p>
          </div>
        </div>

        {/* Categories Table */}
        <CategoryTable
          categories={filteredCategories}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
        />
      </div>
    </Layout>
  );
};

export default CategoryPage;
